import React from "react";
import { ShieldCheckIcon, RocketLaunchIcon, ArrowTrendingUpIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import type { TechnicalConsiderationsProps } from "../technicalSolutionPhase.types";

const considerationCategories = [
  {
    key: "performance" as const,
    name: "Performance",
    icon: RocketLaunchIcon,
    color: "text-green-600 bg-green-50",
    description: "Otimizações e melhorias de performance",
  },
  {
    key: "security" as const,
    name: "Segurança",
    icon: ShieldCheckIcon,
    color: "text-red-600 bg-red-50",
    description: "Medidas de segurança e proteção",
  },
  {
    key: "scalability" as const,
    name: "Escalabilidade",
    icon: ArrowTrendingUpIcon,
    color: "text-blue-600 bg-blue-50",
    description: "Capacidade de crescimento e expansão",
  },
  {
    key: "maintenance" as const,
    name: "Manutenibilidade",
    icon: WrenchScrewdriverIcon,
    color: "text-purple-600 bg-purple-50",
    description: "Facilidade de manutenção e evolução",
  },
];

export default function TechnicalConsiderations({
  considerations,
  onChange,
}: TechnicalConsiderationsProps) {
  const handleAddConsideration = (category: keyof typeof considerations) => {
    const newConsiderations = {
      ...considerations,
      [category]: [...considerations[category], ""],
    };
    onChange(newConsiderations);
  };

  const handleUpdateConsideration = (
    category: keyof typeof considerations,
    index: number,
    value: string
  ) => {
    const newConsiderations = {
      ...considerations,
      [category]: considerations[category].map((item, i) =>
        i === index ? value : item
      ),
    };
    onChange(newConsiderations);
  };

  const handleRemoveConsideration = (
    category: keyof typeof considerations,
    index: number
  ) => {
    const newConsiderations = {
      ...considerations,
      [category]: considerations[category].filter((_, i) => i !== index),
    };
    onChange(newConsiderations);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <ShieldCheckIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-lg font-medium text-gray-900">Considerações Técnicas</h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {considerationCategories.map(({ key, name, icon: Icon, color, description }) => (
          <div key={key} className="border border-gray-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className={clsx("p-1.5 rounded", color)}>
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{name}</h4>
                  <p className="text-xs text-gray-500">{description}</p>
                </div>
              </div>
              <button
                onClick={() => handleAddConsideration(key)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                + Adicionar
              </button>
            </div>

            <div className="space-y-2">
              {considerations?.[key].map((item, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <textarea
                    value={item}
                    onChange={(e) =>
                      handleUpdateConsideration(key, index, e.target.value)
                    }
                    rows={2}
                    className="flex-1 text-sm border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    placeholder={`Adicione uma consideração de ${name.toLowerCase()}...`}
                  />
                  <button
                    onClick={() => handleRemoveConsideration(key, index)}
                    className="mt-2 text-red-600 hover:text-red-700 p-1"
                    title="Remover consideração"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ))}

              {considerations?.[key].length === 0 && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  Nenhuma consideração de {name.toLowerCase()} adicionada
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}